import { Logger } from '../../types/logger';

// Internal storage for the logger
// We start with a basic logger implementation
let _logger: Logger = {
    debug: () => {},
    info: () => {},
    warn: () => {},
    error: () => {},
};

const requiredMethods: (keyof Logger)[] = Object.keys(
    _logger,
) as (keyof Logger)[];

/**
 * Sets the global logger instance
 * If the logger has additional methods beyond the standard ones,
 * provide the type parameter to ensure proper typing.
 *
 * @param logger - The logger implementation to use
 * @returns True if logger was valid and set successfully
 */
export const setLogger = <T extends string = never>(
    logger: Logger<T>,
): boolean => {
    // Validate logger
    const isObject =
        Object.prototype.toString.call(logger) === '[object Object]';
    const hasAllMethods =
        isObject &&
        requiredMethods.every((method) => typeof logger[method] === 'function');

    if (hasAllMethods) {
        _logger = logger;
        return true;
    }

    console.warn(
        '[Tako] Invalid logger provided. Using default no-op logger.',
        { logger },
    );
    return false;
};

/**
 * Gets the current logger instance.
 * If you need to access additional methods on the logger,
 * provide the type parameter to ensure proper typing.
 *
 * @returns The current logger
 */
export const getLogger = <T extends string = never>(): Logger<T> =>
    _logger as Logger<T>;
